import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import ZenitThinking from '../../../components/ui/ZenitThinking';
import logo from '../../../assets/Logo.webp';
import WizardIgService from '../../../services/wizardIg.service';
import { StepHeader } from '../wizardUi';

export default function Step6Interview({ wizard }) {
  const { t } = useTranslation();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [currentKey, setCurrentKey] = useState(null);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);
  const askedRef = useRef(false);

  async function askNext(idx) {
    setThinking(true);
    const nextQ = await WizardIgService.getNextInterviewQuestion(idx);
    setThinking(false);
    if (nextQ) {
      setCurrentKey(nextQ.key);
      setQuestionIndex(idx + 1);
      wizard.addInterviewMessage({ role: 'zeno', text: t(`wizardIg.${nextQ.questionKey}`) });
    } else {
      setCurrentKey(null);
      wizard.setInterviewField('complete', true);
      wizard.addInterviewMessage({ role: 'zeno', text: t('wizardIg.interviewDone') });
      setTimeout(() => wizard.next(), 900);
    }
  }

  useEffect(() => {
    if (!askedRef.current && wizard.interview.messages.length === 0) {
      askedRef.current = true;
      askNext(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [wizard.interview.messages, thinking]);

  function handleSend() {
    const text = input.trim();
    if (!text || wizard.interview.complete || thinking) return;
    wizard.addInterviewMessage({ role: 'user', text });
    if (currentKey) wizard.setInterviewField(currentKey, text);
    setInput('');
    askNext(questionIndex);
  }

  return (
    <div className="flex h-full flex-col gap-5">
      <StepHeader eyebrow={t('wizardIg.eyebrow', { n: 6 })} title={t('wizardIg.title4')} />

      <div className="flex min-h-0 w-full flex-1 flex-col justify-end gap-3 overflow-y-auto lg:mx-auto lg:max-w-2xl">
        {wizard.interview.messages.map((m, i) =>
          m.role === 'zeno' ? (
            <div key={i} className="flex max-w-[88%] items-start gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.06] p-1.5">
                <img src={logo} alt="" className="h-full w-full object-contain" />
              </div>
              <div className="rounded-3xl rounded-bl-md border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-[14px] leading-snug">
                {m.text}
              </div>
            </div>
          ) : (
            <div
              key={i}
              className="ml-auto max-w-[88%] rounded-3xl rounded-br-md bg-primary/85 px-4 py-2.5 text-[14px] leading-snug text-white"
            >
              {m.text}
            </div>
          )
        )}
        {thinking && (
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/[0.06] p-1.5">
              <ZenitThinking size={20} />
            </div>
            <div className="rounded-3xl rounded-bl-md border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-[14px] text-white/50">…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {!wizard.interview.complete && (
        <div className="flex w-full shrink-0 items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] py-1.5 pl-5 pr-1.5 focus-within:border-primary/70 focus-within:ring-2 focus-within:ring-primary/25 lg:mx-auto lg:max-w-2xl">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder={t('wizardIg.chatPh')}
            disabled={thinking}
            className="h-9 flex-1 bg-transparent text-[14.5px] text-foreground placeholder:text-white/35 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={thinking || !input.trim()}
            aria-label="Send"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-[#7c3aed] to-[#8f6bff] text-white shadow-[0_6px_20px_rgba(124,58,237,0.45)] transition-all hover:brightness-110 disabled:opacity-40 disabled:shadow-none"
          >
            <Send size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
